import type { ContourLabel } from '@src/store/contours/contour-definitions';
import type { Orientation } from '@src/store/dicom-web/classification-typing';

import type { Layout } from './types/layout';
import type { ViewSpec } from './types/views';

import { LayoutDirection } from './types/layout';

/**
 * These are the initial view IDs.
 */

export const InitViewIDs: Record<string, string> = {
  ContoursLeft: 'ContoursLeft',
  ContoursRight: 'ContoursRight',
  AnalysisLeft: 'AnalysisLeft',
  AnalysisMiddle: 'AnalysisMiddle',
  AnalysisRight: 'AnalysisRight',
  ViewerComponentTopLeft: 'ViewerComponentTopLeft',
  ViewerComponentTopRight: 'ViewerComponentTopRight',
  ViewerComponentBottomLeft: 'ViewerComponentBottomLeft',
  ViewerComponentBottomRight: 'ViewerComponentBottomRight',
  FunctionMainView: 'FunctionMainView',
  FunctionSecondaryView: 'FunctionSecondaryView',
  FlowMagnitude: 'FlowMagnitude',
  FlowPhase: 'FlowPhase',
  AnalysisGeneralView: 'AnalysisGeneralView',
};
export const InitAppViews: Record<string, string> = {
  QualityCheck: 'QualityCheck',
  Analysis: 'Analysis',
  ViewerComponent: 'ViewerComponent',
  FunctionComponent: 'FunctionComponent',
  FlowComponent: 'FlowComponent',
  General: 'General',
};
export type ViewIDKey = keyof typeof InitViewIDs;
export type ViewID = (typeof InitViewIDs)[ViewIDKey];
export type AppViews = keyof typeof InitAppViews;

export const AppViewViewIDs: Record<AppViews, ViewID[]> = {
  QualityCheck: [
    InitViewIDs.ContoursLeft,
    InitViewIDs.ContoursRight,
    InitViewIDs.AnalysisGeneralView,
  ],
  GraphicalAnalysis: [
    InitViewIDs.AnalysisLeft,
    InitViewIDs.AnalysisMiddle,
    InitViewIDs.AnalysisRight,
    InitViewIDs.AnalysisGeneralView,
  ],
  ViewerComponent: [
    InitViewIDs.ViewerComponentTopLeft,
    InitViewIDs.ViewerComponentTopRight,
    InitViewIDs.ViewerComponentBottomLeft,
    InitViewIDs.ViewerComponentBottomRight,
    InitViewIDs.AnalysisGeneralView,
  ],
  FunctionComponent: [
    InitViewIDs.FunctionMainView,
    InitViewIDs.FunctionSecondaryView,
    InitViewIDs.AnalysisGeneralView,
  ],
  FlowComponent: [
    InitViewIDs.FlowMagnitude,
    InitViewIDs.FlowPhase,
    InitViewIDs.AnalysisGeneralView,
  ],
  General: [InitViewIDs.AnalysisGeneralView],
};

// Used to restrict contour interactions. TODO: if needed,
//  expand to types" move, select, delete, draw etc
export const RestrictedContourInteractionViewIDs: ViewID[] = [
  // InitViewIDs.AnalysisLeft,
  // InitViewIDs.AnalysisRight,
  InitViewIDs.AnalysisGeneralView,
];

export const LowRiskContourLabels: ContourLabel[] = [
  'AnnotationArrow' as ContourLabel,
  'AnnotationText' as ContourLabel,
  'MaskMeasurement' as ContourLabel,
];

// used to limit the indices in the viewer based on BREATHHOLD
export const RestrictToBreathholdViewIDs: ViewID[] = [
  InitViewIDs.AnalysisLeft,
  InitViewIDs.AnalysisRight,
  InitViewIDs.ContoursLeft,
  InitViewIDs.ContoursRight,
];

export const AllowedOrientations: Record<string, string[]> = {};

export const ComponentViewIDs: Record<string, ViewID[]> = {
  UIDDisplayContours: [
    InitViewIDs.ContoursLeft,
    InitViewIDs.ContoursRight,
    InitViewIDs.FunctionMainView,
    InitViewIDs.FunctionSecondaryView,
    InitViewIDs.FlowMagnitude,
    InitViewIDs.FlowPhase,
    InitViewIDs.AnalysisGeneralView,
    InitViewIDs.ViewerComponentTopLeft,
    InitViewIDs.ViewerComponentTopRight,
    InitViewIDs.ViewerComponentBottomLeft,
    InitViewIDs.ViewerComponentBottomRight,
    InitViewIDs.AnalysisLeft,
    InitViewIDs.AnalysisRight,
  ],
  UIDAdjustContours: [
    InitViewIDs.ContoursLeft,
    InitViewIDs.ContoursRight,
    InitViewIDs.AnalysisLeft,
    InitViewIDs.AnalysisRight,
    InitViewIDs.FunctionMainView,
    InitViewIDs.FunctionSecondaryView,
    InitViewIDs.FlowMagnitude,
    InitViewIDs.FlowPhase,
    InitViewIDs.ViewerComponentTopLeft,
    InitViewIDs.ViewerComponentTopRight,
    InitViewIDs.ViewerComponentBottomLeft,
    InitViewIDs.ViewerComponentBottomRight,
  ],
  UIDDisplayMasks: [InitViewIDs.AnalysisLeft, InitViewIDs.AnalysisRight],
  ChangeSeries: [
    InitViewIDs.AnalysisGeneralView,
    InitViewIDs.ContoursLeft,
    InitViewIDs.ContoursRight,
    InitViewIDs.FunctionMainView,
    InitViewIDs.FunctionSecondaryView,
    InitViewIDs.FlowMagnitude,
    InitViewIDs.FlowPhase,
    InitViewIDs.ViewerComponentTopLeft,
    InitViewIDs.ViewerComponentTopRight,
    InitViewIDs.ViewerComponentBottomLeft,
    InitViewIDs.ViewerComponentBottomRight,
  ],
};

export const ToolViewIDs: Record<string, AppViews[]> = {
  AutoSegmentation: [InitAppViews.QualityCheck],
  OCGAnalysis: [InitAppViews.QualityCheck],
  OffsetSettings: [InitAppViews.QualityCheck, InitAppViews.ViewerComponent],
};

/**
 * Returns all neighbor view IDs for a given InitViewID.
 * Neighbors are all IDs in the same app view group.
 */
export function getNeighborViewIDs(viewID: ViewID): ViewID[] {
  for (const group of Object.values(AppViewViewIDs)) {
    if (group.includes(viewID)) {
      return group.filter(id => id !== viewID);
    }
  }
  return [];
}

export function checkComponentViewIDPermission(
  componentName: string,
  viewID: ViewID,
): boolean {
  const viewIDs = ComponentViewIDs[componentName];
  return viewIDs ? viewIDs.includes(viewID) : false;
}

export function isOrientationSupported(
  viewID: ViewID,
  orientation: Orientation,
) {
  return true; // Disable for now
  if (!AllowedOrientations[viewID]) {
    return true; // No restrictions for this view ID
  }
  // If no orientation is specified, return true
  if (!orientation) {
    return true;
  }
  const allowedOrientations = AllowedOrientations[viewID];
  return allowedOrientations
    ? allowedOrientations.includes(orientation)
    : false;
}

export function shouldShowTool(toolName: string, appView: AppViews): boolean {
  const appViews = ToolViewIDs[toolName];
  return appViews ? appViews.includes(appView) : false;
}

/**
 * View spec for the initial view IDs.
 */
export const InitViewSpecs: Record<string, ViewSpec> = {
  [InitViewIDs.ContoursLeft]: {
    viewType: '2D',
    props: {
      viewDirection: 'Right',
      viewUp: 'Superior',
    },
  },
  [InitViewIDs.ContoursRight]: {
    viewType: '2D',
    props: {
      viewDirection: 'Right',
      viewUp: 'Superior',
    },
  },
  [InitViewIDs.AnalysisLeft]: {
    viewType: '2D',
    props: {
      viewDirection: 'Right',
      viewUp: 'Superior',
    },
  },
  [InitViewIDs.AnalysisMiddle]: {
    viewType: '2D',
    props: {
      viewDirection: 'Right',
      viewUp: 'Superior',
    },
  },
  [InitViewIDs.AnalysisRight]: {
    viewType: '2D',
    props: {
      viewDirection: 'Right',
      viewUp: 'Superior',
    },
  },
  [InitViewIDs.ViewerComponentTopLeft]: {
    viewType: '2D',
    props: {
      viewDirection: 'Right',
      viewUp: 'Superior',
    },
  },
  [InitViewIDs.ViewerComponentTopRight]: {
    viewType: '2D',
    props: {
      viewDirection: 'Right',
      viewUp: 'Superior',
    },
  },
  [InitViewIDs.ViewerComponentBottomLeft]: {
    viewType: '2D',
    props: {
      viewDirection: 'Right',
      viewUp: 'Superior',
    },
  },
  [InitViewIDs.ViewerComponentBottomRight]: {
    viewType: '2D',
    props: {
      viewDirection: 'Right',
      viewUp: 'Superior',
    },
  },
  [InitViewIDs.FunctionSecondaryView]: {
    viewType: '2D',
    props: {
      viewDirection: 'Right',
      viewUp: 'Superior',
    },
  },
  [InitViewIDs.FunctionMainView]: {
    viewType: '2D',
    props: {
      viewDirection: 'Right',
      viewUp: 'Superior',
    },
  },
  [InitViewIDs.AnalysisGeneralView]: {
    viewType: '2D',
    props: {
      viewDirection: 'Right',
      viewUp: 'Superior',
    },
  },
};

/**
 * The default view spec.
 */
export const DefaultViewSpec = InitViewSpecs[InitViewIDs.ContoursLeft];

/**
 * Defines the default layouts.
 */
export const Layouts: Record<string, Layout> = [
  {
    name: 'Contours',
    direction: LayoutDirection.H,
    items: [InitViewIDs.ContoursLeft, InitViewIDs.ContoursRight],
  },
  {
    name: 'Analysis',
    direction: LayoutDirection.H,
    items: [
      InitViewIDs.AnalysisLeft,
      InitViewIDs.AnalysisMiddle,
      InitViewIDs.AnalysisRight,
    ],
  },
].reduce((layouts, layout) => {
  return { ...layouts, [layout.name]: layout };
}, {});

export const LABELMAP_PALETTE = {
  0: [0, 0, 0, 0], // eraser
  1: [153, 153, 0, 255],
  2: [76, 76, 0, 255],
  3: [255, 255, 0, 255],
  4: [0, 76, 0, 255],
  5: [0, 153, 0, 255],
  6: [0, 255, 0, 255],
  7: [76, 0, 0, 255],
  8: [153, 0, 0, 255],
  9: [255, 0, 0, 255],
  10: [0, 76, 76, 255],
  11: [0, 153, 153, 255],
  12: [0, 255, 255, 255],
  13: [0, 0, 76, 255],
  14: [0, 0, 153, 255],
} as Record<number, number[]>;

export const TOOL_COLORS = [
  '#58f24c',
  '#8de4d3',
  '#f0a4b1',
  '#a3c9fe',
  '#c8f251',
  '#fea53b',
];

export const RULER_LABEL_DEFAULTS = {
  red: { color: 'red' },
  green: { color: '#00ff00' },
  white: { color: '#ffffff' },
};

export const RECTANGLE_LABEL_DEFAULTS = {
  artifact: { color: '#888888', fillColor: '#ffbf0040' },
  innocuous: { color: '#00ff00', fillColor: '#00ff0020' },
  lesion: { color: 'red', fillColor: 'transparent' },
};

export const DECREMENT_LABEL_KEY = 'q';
export const INCREMENT_LABEL_KEY = 'w';

export const DEFAULT_PRESET_BY_MODALITY: Record<string, string> = {
  CT: 'CT-AAA',
  MR: 'CT-Coronary-Arteries-2',
  US: 'US-Fetal',
};
